// One loading, empty, error and not-applicable vocabulary for every Power Admin screen: what the
// operator sees while data loads, when there is none, when Power could not be read, and when a
// screen does not apply to this deployment. A failure always says how to ask again and, when Power
// answered, the error code and trace id to quote to support, with the link that finds the trace in
// Operações.
import { Button } from '@finnest/ui';
import { useRouter } from '@tanstack/react-router';
import { Option } from 'effect';
import { CircleOff, RefreshCw, SearchX, TriangleAlert } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useId } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import type { PowerRefusal } from '@finnest/power-admin-contracts/failure';
import { searchHref } from '../operacoes/shared/search-url.ts';
import { FailureReference } from './failure/failure-reference.tsx';
import statesStyles from './states.css?inline';

const statesStyle = document.createElement('style');
statesStyle.textContent = statesStyles;
document.head.append(statesStyle);

const SKELETON_TILES = 4;
const PAGE_LINES = 5;
const SECTION_LINES = 3;

// The failure time is the browser's own stamp of the failed read, shown in São Paulo time.
const FAILURE_TIME = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const skeletonLines = (count: number): JSX.Element[] =>
  Array.from({ length: count }, (_: undefined, index: number) => (
    <span key={index} className="admin-state-skeleton-block admin-state-skeleton-line" />
  ));

const SKELETONS = {
  page: (): JSX.Element => (
    <>
      <span className="admin-state-skeleton-tiles">
        {Array.from({ length: SKELETON_TILES }, (_: undefined, index: number) => (
          <span key={index} className="admin-state-skeleton-block admin-state-skeleton-tile" />
        ))}
      </span>
      {skeletonLines(PAGE_LINES)}
    </>
  ),
  section: (): JSX.Element => <>{skeletonLines(SECTION_LINES)}</>,
  row: (): JSX.Element => <span className="admin-state-skeleton-block admin-state-skeleton-bar" />,
} satisfies Record<LoadingStateProps['layout'], () => JSX.Element>;

type LoadingStateProps = {
  /** What is loading, as it reads after "Carregando", e.g. "as conexões". */
  readonly label: string;
  /** A whole screen (tiles and lines), one section (lines) or one inline row (a bar). */
  readonly layout: 'page' | 'section' | 'row';
};

export const LoadingState = ({ label, layout }: LoadingStateProps): JSX.Element => {
  const labelId = useId();
  return (
    <output
      className={`admin-state-loading admin-state-loading-${layout}`}
      aria-busy="true"
      aria-labelledby={labelId}
    >
      <span id={labelId} className="admin-state-visually-hidden">
        Carregando {label}
      </span>
      {SKELETONS[layout]()}
    </output>
  );
};

const QuietState = ({
  icon: Icon,
  title,
  text,
  action,
}: {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly text: string;
  readonly action: Option.Option<ReactNode>;
}): JSX.Element => (
  <output className="admin-state-empty">
    <span className="admin-state-empty-icon">
      <Icon aria-hidden="true" size={22} />
    </span>
    <strong>{title}</strong>
    <span className="admin-state-empty-text">{text}</span>
    {Option.getOrNull(action)}
  </output>
);

type EmptyStateProps = {
  readonly title: string;
  /** What the operator can do next: every empty state names it. */
  readonly nextStep: string;
  readonly action: Option.Option<ReactNode>;
  readonly icon?: LucideIcon;
};

export const EmptyState = ({
  title,
  nextStep,
  action,
  icon = SearchX,
}: EmptyStateProps): JSX.Element => (
  <QuietState icon={icon} title={title} text={nextStep} action={action} />
);

/** What the operator is told when a failed refresh leaves the data read before on screen. */
const STALE_DATA_TEXT = 'Os dados exibidos podem estar desatualizados.';

type RetryProps = {
  /** What failed to load, as it reads after "Não foi possível carregar", e.g. "as conexões". */
  readonly subject: string;
  /** The error code and trace id Power answered the failed request with; none without an answer. */
  readonly refusal: Option.Option<PowerRefusal>;
  readonly failedAt: Date;
  readonly retrying: boolean;
  readonly onRetry: () => void;
  /** False where there is no operator session (the invitation page): no link to Operações. */
  readonly traceable: boolean;
};

// A plain anchor pushed on the history, not a router Link: the router writes search values through
// its JSON serializer, which would quote an id of only digits and the search would look for the
// quotes. A modified click (new tab, new window) keeps the browser's own behaviour.
const TraceLink = ({ id }: { readonly id: string }): JSX.Element => {
  const router = useRouter();
  const href = searchHref(id);
  return (
    <a
      href={href}
      onClick={(click: MouseEvent<HTMLAnchorElement>) => {
        if (
          click.defaultPrevented ||
          click.button !== 0 ||
          click.metaKey ||
          click.ctrlKey ||
          click.shiftKey ||
          click.altKey
        )
          return;
        click.preventDefault();
        router.history.push(href);
      }}
    >
      Rastrear em Operações
    </a>
  );
};

const Reference = ({
  refusal,
  failedAt,
  traceable,
}: Pick<RetryProps, 'refusal' | 'failedAt' | 'traceable'>): JSX.Element =>
  Option.match(refusal, {
    onNone: () => (
      <span className="admin-state-support">
        Sem código do erro: o Power não respondeu ou não informou um. Horário da falha:{' '}
        {FAILURE_TIME.format(failedAt)}
      </span>
    ),
    onSome: (answered) => (
      <div className="admin-state-support">
        <FailureReference refusal={answered} />
        {traceable && answered.traceId !== null ? <TraceLink id={answered.traceId} /> : null}
      </div>
    ),
  });

type NotApplicableProps = {
  readonly title: string;
  /** Why this screen does not apply to this deployment or session. */
  readonly reason: string;
  readonly action: Option.Option<ReactNode>;
  /** The error code and trace id of the refusal that showed it does not apply, to quote to support. */
  readonly refusal: Option.Option<PowerRefusal>;
  /** False where there is no operator session (the invitation page): no link to Operações. */
  readonly traceable: boolean;
};

// The reference sits beside the status, not in it: an `output` cannot hold the reference's list.
export const NotApplicableState = ({
  title,
  reason,
  action,
  refusal,
  traceable,
}: NotApplicableProps): JSX.Element => (
  <>
    <QuietState icon={CircleOff} title={title} text={reason} action={action} />
    {Option.isSome(refusal) ? (
      <div className="admin-state-support admin-state-final-reference">
        <FailureReference refusal={refusal.value} />
        {traceable && refusal.value.traceId !== null ? (
          <TraceLink id={refusal.value.traceId} />
        ) : null}
      </div>
    ) : null}
  </>
);

const RetryButton = ({
  retrying,
  onRetry,
}: Pick<RetryProps, 'retrying' | 'onRetry'>): JSX.Element => (
  <Button disabled={retrying} onClick={onRetry}>
    <RefreshCw aria-hidden="true" size={14} /> Tentar de novo
  </Button>
);

export const ErrorState = ({
  subject,
  reason,
  refusal,
  failedAt,
  retrying,
  onRetry,
  traceable,
}: RetryProps & { readonly reason: Option.Option<string> }): JSX.Element => (
  <div className="admin-state-error" role="alert">
    <TriangleAlert aria-hidden="true" size={20} />
    <div className="admin-state-message">
      <strong>Não foi possível carregar {subject}.</strong>
      {Option.isSome(reason) ? <p>{reason.value}</p> : null}
      <Reference refusal={refusal} failedAt={failedAt} traceable={traceable} />
    </div>
    <RetryButton retrying={retrying} onRetry={onRetry} />
  </div>
);

// The refresh failed but the screen still has data it already loaded: say the data may be stale
// without replacing the screen, so what the operator is typing is not lost. The sentence is the
// status; the reference (a list, which a status `output` cannot hold) and the retry sit beside it.
export const StaleDataNotice = ({
  subject,
  refusal,
  failedAt,
  retrying,
  onRetry,
  traceable,
}: RetryProps): JSX.Element => (
  <div className="admin-state-stale">
    <div className="admin-state-message">
      <output>
        Não foi possível atualizar {subject}. {STALE_DATA_TEXT}
      </output>
      <Reference refusal={refusal} failedAt={failedAt} traceable={traceable} />
    </div>
    <RetryButton retrying={retrying} onRetry={onRetry} />
  </div>
);

/**
 * When the last of `queries` failed: React Query stamps a thrown failure in `errorUpdatedAt` and a
 * refusal the client decodes into the data in `dataUpdatedAt`.
 */
export const failedAtOf = (
  queries: readonly { readonly errorUpdatedAt: number; readonly dataUpdatedAt: number }[],
): Date =>
  new Date(
    Math.max(0, ...queries.map((query) => Math.max(query.errorUpdatedAt, query.dataUpdatedAt))),
  );
